import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {FaIcon} from '../../FaIcon';
import {Spinner} from '../../Spinner';
import {SpinnerButton} from '../../SpinnerButton';
import {tabUrl} from './sheetUrl';
import {currentMonthKey} from './tabStrip';

// Selector manual de la solapa del plan.
//
// Vive en un MODAL. Nació como panel expandido dentro del strip, con el argumento de que la
// decisión necesita ver en pantalla qué se eligió y qué se descartó — pero con datos reales ese
// argumento no se sostuvo: con 60 solapas el panel estiraba la página y empujaba la propuesta
// fuera del viewport, así que el contexto que decía preservar no quedaba visible igual. Lo poco
// que hacía falta (qué mes se está leyendo) entra en el encabezado del modal.
//
// Además elegir solapa NO es un filtro: relee el plan y marca STALE la propuesta abierta. Un modal
// comunica esa consecuencia mejor que un panel inline, y es el patrón que la pantalla ya usa para
// lo que importa (desconectar la planilla, el Picker de Drive).
//
// Se abre desde DOS superficies —el chip del strip y el CTA del empty state—, así que sigue siendo
// alcanzable con propuesta y sin ella. Eso importa para el peor caso: una propuesta plausible
// construida sobre la solapa equivocada.
//
// Por qué no `SelectWithSearch`: renderiza cada opción como una línea de texto, sin segunda línea
// ni agrupación ni badge. Con 60 solapas de las cuales 3 importan, un dropdown plano borra
// exactamente la información que evita el segundo error. Esto no es un campo de formulario: es una
// decisión con evidencia. Se reusa el lenguaje visual del combobox de métricas (búsqueda con
// contador, headers de grupo colapsables) pero desplegado en línea.

const NS = 'media_plan.connections.tab';

// Clase Unicode nombrada en vez de un rango de diacríticos combinantes escritos literalmente: esos
// caracteres son invisibles en el editor y cualquier reencodeo del archivo los rompe en silencio.
const normalize = (text) => (text || '')
    .toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

// Tres grupos, orden fijo. El del mes objetivo arranca abierto porque ahí está la respuesta casi
// siempre (3 filas); los otros dos van colapsados para que las 60 solapas no se vean de golpe.
const GROUP_TARGET = 'target';
const GROUP_OTHER_MONTHS = 'other_months';
const GROUP_UNDATED = 'undated';

function groupOf(tab, targetMonth) {
    if (tab.plan_month && tab.plan_month === targetMonth) return GROUP_TARGET;
    if (tab.plan_month) return GROUP_OTHER_MONTHS;
    return GROUP_UNDATED;
}

// `loading` / `loadError`: la conexión todavía no tiene escaneo y la lista se está pidiendo (o falló);
// `onRetryLoad` la vuelve a pedir. Sin escaneo previo las solapas llegan sin mes leído, así que van en
// una sola lista (agruparlas las mandaría todas a «Sin mes en el nombre», que sería falso).
export function MediaPlanTabPicker({scan, externalId, preselectGid, preselectNonce, saving,
                                    refreshing, onRefresh, onConfirm, onRestoreAuto, onClose,
                                    loading = false, loadError = false, onRetryLoad = null}) {
    const {t} = useTranslation();
    // Memoizado para que la identidad no cambie en cada render (si no, el `useMemo` de los grupos
    // se recalcula siempre y el default `[]` cuenta como dependencia nueva).
    const tabs = useMemo(() => (scan || {}).tabs || [], [scan]);
    const targetMonth = (scan || {}).target_month || null;
    // El mes que se nombra: el del escaneo o, si nunca se leyó, el corriente (es el que fija el
    // backend en ese caso).
    const period = targetMonth || currentMonthKey();
    const unavailable = loading || loadError;
    const selectedGid = ((scan || {}).selected || {}).gid;
    const pinnedGid = (scan || {}).pinned_gid;

    // Preselección: cuando el empty state ofrece una candidata concreta, el panel se abre con ESA
    // marcada y el usuario solo confirma. Si no, arranca en la que se está leyendo.
    const initialChoice = preselectGid !== null && preselectGid !== undefined
        ? preselectGid
        : (selectedGid !== undefined ? selectedGid : null);
    const [choice, setChoice] = useState(initialChoice);

    // El panel y el empty state conviven en pantalla (el empty state aparece justo cuando no hay
    // propuesta, que es cuando se usa el selector). Si el usuario deja el panel abierto y después
    // clickea "usar esta solapa" abajo, el componente NO se remonta y la radio quedaba marcada en
    // la anterior: confirmar habría fijado la solapa equivocada — el error exacto que este panel
    // vino a evitar.
    //
    // `preselectNonce` está en las dependencias porque el gid SOLO no alcanza: pedir DOS VECES la
    // misma candidata (elegir X abajo, cambiar la radio a mano, y volver a elegir X) no cambia el
    // valor, así que el efecto no volvería a correr y la radio quedaría donde la dejó el usuario.
    // El nonce hace que cada pedido sea un evento distinto, aunque apunte a la misma solapa.
    useEffect(() => {
        if (preselectGid !== null && preselectGid !== undefined) setChoice(preselectGid);
    }, [preselectGid, preselectNonce]);
    const [query, setQuery] = useState('');
    const [openGroups, setOpenGroups] = useState({[GROUP_TARGET]: true});

    const needle = normalize(query.trim());
    const matchingTabs = useMemo(
        () => tabs.filter((tab) => !needle || normalize(tab.title).includes(needle)), [tabs, needle]);
    const groups = useMemo(() => {
        const buckets = {[GROUP_TARGET]: [], [GROUP_OTHER_MONTHS]: [], [GROUP_UNDATED]: []};
        matchingTabs.forEach((tab) => buckets[groupOf(tab, targetMonth)].push(tab));
        // Meses más recientes primero: buscar el plan del mes pasado es lo segundo más común.
        buckets[GROUP_OTHER_MONTHS].sort((a, b) => (b.plan_month || '').localeCompare(a.plan_month || ''));
        return buckets;
    }, [matchingTabs, targetMonth]);

    const shown = matchingTabs.length;
    const searching = query.trim().length > 0;
    // Buscando, los grupos con resultados se abren solos: si no, el usuario tipea y no ve nada.
    const isOpen = (key) => (searching ? groups[key].length > 0 : !!openGroups[key]);
    const toggleGroup = (key) => setOpenGroups((open) => ({...open, [key]: !open[key]}));

    const renderOption = (tab) => {
        const isChoice = choice === tab.gid;
        const href = tabUrl(externalId, tab.gid);
        return (
            <label key={tab.gid} className={`mp-tab-option ${isChoice ? '-selected' : ''}`}>
                {/* Radio CANÓNICO de Dashbo (mismo patrón que `.mp-link-option`, en esta misma
                    pantalla): el input nativo se oculta y el círculo lo dibuja un span. No es
                    cosmético — el global `.input-style, input, select, textarea {width:100%}` le da
                    a un `input[type=radio]` el ancho COMPLETO de la fila, y el contenido queda
                    aplastado a 0px y fuera de la vista. */}
                <input type="radio" className="mp-tab-radio-input" name="mp-tab-option"
                       checked={isChoice} onChange={() => setChoice(tab.gid)}/>
                <span className="mp-tab-radio" aria-hidden="true"/>
                <span className="mp-tab-option-main">
                    <span className="mp-tab-option-head">
                        <span className="mp-tab-option-name" title={tab.title}>{tab.title}</span>
                        {tab.gid === selectedGid && (
                            <span className="mp-tab-option-chip">
                                {t(pinnedGid === tab.gid ? `${NS}.manual` : `${NS}.auto`)}
                            </span>
                        )}
                        {tab.plan_month && tab.plan_month !== targetMonth && (
                            <span className="mp-tab-option-month">{tab.plan_month}</span>
                        )}
                    </span>
                    {/* El motivo del descarte como segunda línea: es el contexto que ayuda a elegir
                        bien, y lo que faltó cuando el sistema eligió mal en silencio. */}
                    {tab.probed && tab.reason && tab.gid !== selectedGid && (
                        <span className="mp-tab-option-reason -warn">
                            <FaIcon iconName="triangle-exclamation"/>
                            {t(`${NS}.reason.${tab.reason}`, {defaultValue: tab.reason})}
                        </span>
                    )}
                    {!tab.probed && tab.plan_month === targetMonth && tab.gid !== selectedGid && (
                        <span className="mp-tab-option-reason">{t(`${NS}.not_read`)}</span>
                    )}
                </span>
                {href && (
                    <a className="mp-tab-option-link" href={href} target="_blank"
                       rel="noopener noreferrer" title={t(`${NS}.open`)}
                       onClick={(e) => e.stopPropagation()}>
                        <FaIcon iconName="up-right-from-square"/>
                    </a>
                )}
            </label>
        );
    };

    const renderGroup = (key, labelKey) => {
        const items = groups[key];
        if (items.length === 0) return null;
        const open = isOpen(key);
        return (
            <div className="mp-tab-group" key={key}>
                <button type="button" className="mp-tab-group-head" aria-expanded={open}
                        onClick={() => toggleGroup(key)}>
                    <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}
                            className="mp-tab-group-caret"/>
                    {t(labelKey, {period: targetMonth || '', interpolation: {escapeValue: false}})}
                    <span className="mp-tab-group-count">{items.length}</span>
                </button>
                {open && (
                    <div className="mp-tab-list" role="radiogroup" aria-label={t(labelKey, {period: targetMonth || ''})}>
                        {items.map(renderOption)}
                        {key === GROUP_UNDATED && (
                            <span className="mp-tab-group-hint">{t(`${NS}.group_undated_hint`)}</span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal isOpen toggle={onClose} className="columns-selector-modal mp-tab-picker">
            <ModalHeader toggle={onClose}>
                <span className="mp-tab-picker-title">{t(`${NS}.picker_title`)}</span>
                <span className="mp-tab-picker-hint">
                    {t(`${NS}.picker_hint`, {period, interpolation: {escapeValue: false}})}
                </span>
            </ModalHeader>
            <ModalBody>
                {loading && (
                    <div className="mp-tab-picker-status" data-testid="mp-tab-picker-loading">
                        <div className="box-loader">
                            <Spinner color="positive"/>
                        </div>
                        <div>{t(`${NS}.picker_loading`)}</div>
                    </div>
                )}
                {loadError && (
                    <div className="mp-tab-picker-status -error" data-testid="mp-tab-picker-error">
                        <div>{t(`${NS}.picker_load_error`)}</div>
                        {/* El mismo botón de «Actualizar lista» de la barra de búsqueda: un `.btn`
                            dentro del modal (que se dibuja en un portal, fuera de la pantalla) salía
                            blanco sobre blanco. */}
                        {onRetryLoad && (
                            <button type="button" className="mp-tab-picker-refresh"
                                    onClick={() => onRetryLoad()}>
                                <FaIcon iconName="rotate" className="mp-tab-picker-refresh-icon"/>
                                {t(`${NS}.refresh`)}
                            </button>
                        )}
                    </div>
                )}
                {!unavailable && (
                <>
                <div className="mp-tab-picker-search">
                    <FaIcon iconName="search" className="mp-tab-picker-search-icon"/>
                    <input type="text" className="form-control" value={query}
                           placeholder={t(`${NS}.search_placeholder`)}
                           onChange={(e) => setQuery(e.target.value)}/>
                    <span className="mp-tab-picker-count">
                        {searching
                            ? t(`${NS}.count_filtered`, {shown, total: tabs.length})
                            : t(`${NS}.count`, {count: tabs.length})}
                    </span>
                    {/* La lista sale del último escaneo. Si la agencia acaba de crear la solapa del
                        mes nuevo, todavía no está — y forzar una relectura completa para verla
                        sería pagar el LLM al pedo. Esto re-lista el archivo sin parsear nada. */}
                    {onRefresh && (
                        <button type="button" className="mp-tab-picker-refresh" disabled={refreshing}
                                onClick={onRefresh}>
                            <FaIcon iconName="rotate" className="mp-tab-picker-refresh-icon"/>
                            {refreshing ? t(`${NS}.refreshing`) : t(`${NS}.refresh`)}
                        </button>
                    )}
                </div>

                <div className="mp-tab-picker-body">
                    {shown === 0 ? (
                        <span className="mp-tab-picker-empty">
                            {t(`${NS}.picker_empty`, {query, interpolation: {escapeValue: false}})}
                        </span>
                    ) : !targetMonth ? (
                        <div className="mp-tab-list" role="radiogroup"
                             aria-label={t(`${NS}.picker_title`)}>
                            {matchingTabs.map(renderOption)}
                        </div>
                    ) : (
                        <>
                            {renderGroup(GROUP_TARGET, `${NS}.group_period`)}
                            {renderGroup(GROUP_OTHER_MONTHS, `${NS}.group_other_months`)}
                            {renderGroup(GROUP_UNDATED, `${NS}.group_undated`)}
                        </>
                    )}
                </div>
                </>
                )}
            </ModalBody>

            <ModalFooter>
                {/* Volver a automático es de otra naturaleza que confirmar/cancelar: va aparte, a la
                    izquierda, y sólo si hay algo fijado que deshacer. */}
                {pinnedGid !== null && pinnedGid !== undefined ? (
                    <button type="button" className="mp-tab-picker-auto" disabled={saving}
                            onClick={onRestoreAuto}>
                        {t(`${NS}.back_to_auto`)}
                    </button>
                ) : <span/>}
                <div className="mp-tab-picker-actions">
                    {/* El alcance del pin, dicho explícitamente: es lo que evita que fijar una
                        solapa se convierta en leer el plan del mes pasado sin enterarse. */}
                    <span className="mp-tab-picker-scope">
                        {t(`${NS}.confirm_scope`, {period, interpolation: {escapeValue: false}})}
                    </span>
                    <button type="button" className="btn -negative" disabled={saving}
                            onClick={onClose}>
                        {t(`${NS}.cancel`)}
                    </button>
                    {saving ? (
                        <SpinnerButton/>
                    ) : (
                        <button type="button" className="btn -alt"
                                disabled={unavailable || choice === null || choice === selectedGid}
                                onClick={() => onConfirm(choice)}>
                            {t(`${NS}.confirm`)}
                        </button>
                    )}
                </div>
            </ModalFooter>
        </Modal>
    );
}
